import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, ArrowUpRight, Building2, Ruler, TrendingUp, Sparkles, Layers,
  ShieldCheck, Mail, Send, Play, HelpCircle, BookOpen,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { submitContactMessage } from "@/lib/api";
import { goToSpaceCost } from "@/lib/spacecost";
import DemoDialog from "@/components/DemoDialog";
import DemoChooserDialog from "@/components/DemoChooserDialog";
import SpaceCostMockup from "@/components/SpaceCostMockup";

// Hero & tool imagery (Unsplash — already cached in the app)
const HERO_IMAGE =
  "https://images.unsplash.com/photo-1484154218962-a197022b5858?auto=format&fit=crop&w=2400&q=80";
const FLIPCOST_IMAGE =
  "https://customer-assets.emergentagent.com/job_project-profit-5/artifacts/abaioan7_Untitled%20design%20%2812%29.png";
const SPACECOST_IMAGE =
  "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?auto=format&fit=crop&w=1600&q=80";
const LOGO_IMAGE =
  "https://customer-assets.emergentagent.com/job_project-profit-5/artifacts/71r5twz2_Your%20paragraph%20text.png";

const CONTACT_EMAIL = "hello@myplancost.com";

// Smooth in-page scroll to a section by id.
function scrollToId(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function MyPlanCost() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);
  const [demoProduct, setDemoProduct] = useState("flipcost");
  const [chooserOpen, setChooserOpen] = useState(false);
  const [picker, setPicker] = useState(null); // null | "how-it-works" | "faq"
  // Sticky demo pill — only renders after the visitor scrolls past the hero
  // so it doesn't compete with the primary hero CTA. We listen on scroll and
  // toggle once past ~80% of the viewport height.
  const [showStickyDemo, setShowStickyDemo] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      setShowStickyDemo(window.scrollY > window.innerHeight * 0.7);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  // Top-level demo CTAs (header, hero, section CTA) all route through the
  // chooser so the visitor picks FlipCost vs SpaceCost first.
  const openDemo = () => setChooserOpen(true);
  const startDemoForProduct = (product) => {
    setDemoProduct(product);
    setDemoOpen(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const { name, email, message } = form;
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error("Please fill in name, email and message");
      return;
    }
    setSubmitting(true);
    try {
      await submitContactMessage({ name: name.trim(), email: email.trim(), message: message.trim() });
      toast.success("Message sent — we'll get back to you shortly");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send your message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] text-[#1C1C1C] fc-fade-in" data-testid="myplancost-page">
      {/* ---------- Top Nav ---------- */}
      <header className="absolute top-0 left-0 right-0 z-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-5 flex items-center justify-between">
          <button
            data-testid="mpc-logo-btn"
            onClick={() => scrollToId("hero")}
            className="flex items-center gap-2.5 text-white group"
          >
            <div
              className="w-[54px] h-[54px] rounded-xl overflow-hidden bg-white shrink-0 transition-transform group-hover:scale-105"
              style={{ filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.25))" }}
            >
              <img
                src={LOGO_IMAGE}
                alt="MyPlanCost"
                className="w-full h-full object-cover scale-[1.12]"
              />
            </div>
            <span className="fc-heading text-lg font-medium tracking-tight">MyPlanCost</span>
          </button>
          <nav className="hidden md:flex items-center gap-7 text-sm text-white/85">
            <button data-testid="mpc-nav-tools" onClick={() => scrollToId("tools")} className="hover:text-white transition-colors">Tools</button>
            <button data-testid="mpc-nav-about" onClick={() => scrollToId("about")} className="hover:text-white transition-colors">About</button>
            <button data-testid="mpc-nav-pricing" onClick={() => navigate("/pricing")} className="hover:text-white transition-colors">Pricing</button>
            <button data-testid="mpc-nav-contact" onClick={() => scrollToId("contact")} className="hover:text-white transition-colors">Contact</button>
            <button
              data-testid="mpc-nav-demo"
              onClick={openDemo}
              className="inline-flex items-center gap-1.5 border border-sage-300/40 bg-sage-300/10 text-sage-300 hover:bg-sage-300/20 hover:text-white rounded-lg h-9 px-3 transition-colors"
            >
              <Play className="w-3.5 h-3.5" /> Try Demo
            </button>
            <button
              data-testid="mpc-nav-signin"
              onClick={() => navigate("/auth")}
              className="border border-white/30 rounded-lg h-9 px-4 hover:bg-white hover:text-[#1C1C1C] transition-colors"
            >
              Sign in
            </button>
          </nav>
          {/* Mobile: compact Pricing + Sign in buttons — the full nav is hidden under md.
              Without these, mobile visitors had no way to reach pricing or login
              from the umbrella home. */}
          <div className="md:hidden flex items-center gap-2">
            <button
              data-testid="mpc-nav-pricing-mobile"
              onClick={() => navigate("/pricing")}
              className="text-sm text-white/85 hover:text-white border border-white/30 rounded-lg h-9 px-3 hover:bg-white/10 transition-colors"
            >
              Pricing
            </button>
            <button
              data-testid="mpc-nav-signin-mobile"
              onClick={() => navigate("/auth")}
              className="text-sm text-white border border-white/30 rounded-lg h-9 px-3 hover:bg-white hover:text-[#1C1C1C] transition-colors"
            >
              Sign in
            </button>
          </div>
        </div>
      </header>

      {/* ---------- Section 1: Hero ----------
          Softer treatment: green-tinted background gradient over the photo,
          warmer cream typography (not pure black-on-black), explicit
          who-this-is-for + value-prop copy so a cold visitor immediately
          knows what MyPlanCost actually does. */}
      <section
        id="hero"
        data-testid="mpc-hero"
        className="relative min-h-[86vh] sm:min-h-[92vh] flex items-center overflow-hidden bg-[#1A2018]"
      >
        <div className="absolute inset-0">
          <img src={HERO_IMAGE} alt="" className="w-full h-full object-cover opacity-70" />
          {/* Sage-tinted gradient overlay — softer than the prior near-black
              veil, lets the kitchen photo breathe and ties the hero into
              the green brand palette without going neon. */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#1f2a1c]/85 via-[#1a2018]/72 to-[#0f1410]/88" />
          <div className="absolute inset-0 opacity-50 pointer-events-none" aria-hidden="true" style={{
            backgroundImage:
              "radial-gradient(circle at 15% 30%, rgba(168,186,153,0.22) 0%, transparent 50%), radial-gradient(circle at 85% 80%, rgba(168,186,153,0.18) 0%, transparent 55%)",
          }} />
        </div>

        <div className="relative max-w-6xl mx-auto px-4 sm:px-8 w-full py-24 sm:py-36">
          <div
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-sage-300 bg-sage-300/10 border border-sage-300/30 rounded-full px-3 py-1 backdrop-blur-md"
            data-testid="mpc-hero-pill"
          >
            <Sparkles className="w-3 h-3" /> Renovation cost planning suite
          </div>

          <h1
            data-testid="mpc-hero-headline"
            className="fc-heading text-[#F5F4EE] text-[34px] leading-[1.1] sm:text-6xl sm:leading-[1.05] lg:text-7xl font-medium tracking-tight mt-6 max-w-4xl"
          >
            Know what your renovation{" "}
            <span className="text-sage-300">will really cost</span>
            <span className="text-[#F5F4EE]/90"> — before you start.</span>
          </h1>

          <p
            data-testid="mpc-hero-sub"
            className="text-[15px] sm:text-lg text-[#F5F4EE]/85 mt-5 sm:mt-6 max-w-2xl leading-relaxed"
          >
            MyPlanCost is the planning toolkit for homeowners, investors and pros tackling a renovation, flip or single-room redo. Get a structured cost estimate, a clear feasibility check, and the confidence to commit — in minutes, not weeks.
          </p>

          {/* Mini product picker — two solid-white cards on a dark hero so
              they pop. Tapping a card scrolls the visitor to the Tools
              section right below (vs jumping away to /spacecost or
              /flipcost) — keeps them on the landing flow so the next two
              sections (Tools detail + About) can do their job. SpaceCost
              is listed first because single-room jobs are the more common
              entry point for our audience. */}
          <div
            className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-3xl"
            data-testid="mpc-hero-product-picker"
          >
            <button
              type="button"
              data-testid="mpc-hero-pick-spacecost"
              onClick={() => scrollToId("tools")}
              className="group text-left rounded-xl bg-white hover:bg-[#FAFAF9] border border-white/0 border-l-[3px] border-l-sage-300 px-4 py-4 shadow-[0_18px_40px_-18px_rgba(0,0,0,0.4)] hover:shadow-[0_22px_45px_-15px_rgba(168,186,153,0.45)] hover:-translate-y-0.5 transition-all"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="inline-flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-sage-100 border border-sage-200 text-sage-700">
                    <Layers className="w-3.5 h-3.5" />
                  </span>
                  <span className="text-sage-700 text-[11px] uppercase tracking-[0.16em] font-medium">For a single space</span>
                </div>
                <ArrowRight className="w-4 h-4 text-[#A8A29E] group-hover:text-sage-700 group-hover:translate-x-0.5 transition-all" />
              </div>
              <div className="fc-heading text-xl text-[#1C1C1C] mt-2.5">SpaceCost</div>
              <div className="text-[13px] text-[#57534E] mt-1 leading-snug">
                Plan one room or one space — kitchen, bathroom, garden build.
                <span className="block text-[#A8A29E] mt-1.5">From R180 / $12 · once-off, up to 3 rooms.</span>
              </div>
            </button>
            <button
              type="button"
              data-testid="mpc-hero-pick-flipcost"
              onClick={() => scrollToId("tools")}
              className="group text-left rounded-xl bg-white hover:bg-[#FAFAF9] border border-white/0 border-l-[3px] border-l-slate-400 px-4 py-4 shadow-[0_18px_40px_-18px_rgba(0,0,0,0.4)] hover:shadow-[0_22px_45px_-15px_rgba(168,186,153,0.45)] hover:-translate-y-0.5 transition-all"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="inline-flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-sage-100 border border-sage-200 text-sage-700">
                    <Building2 className="w-3.5 h-3.5" />
                  </span>
                  <span className="text-sage-700 text-[11px] uppercase tracking-[0.16em] font-medium">For a whole property</span>
                </div>
                <ArrowRight className="w-4 h-4 text-[#A8A29E] group-hover:text-sage-700 group-hover:translate-x-0.5 transition-all" />
              </div>
              <div className="fc-heading text-xl text-[#1C1C1C] mt-2.5">FlipCost</div>
              <div className="text-[13px] text-[#57534E] mt-1 leading-snug">
                Full-house renovation or flip — rooms, exterior, profit &amp; ROI math.
                <span className="block text-[#A8A29E] mt-1.5">From R280 / $18 · once-off, unlimited rooms.</span>
              </div>
            </button>
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-3" data-testid="mpc-hero-cta-row">
            {/* Demo promoted to primary CTA — most people convert better after
                actually trying the product than after another landing-page
                scroll. Sized larger + glow ring to be the obvious next action. */}
            <Button
              data-testid="mpc-hero-cta-demo"
              onClick={openDemo}
              className="relative bg-sage-300 text-[#1A2018] hover:bg-sage-200 h-14 px-7 rounded-xl text-base font-semibold inline-flex items-center gap-2 shadow-[0_0_0_4px_rgba(168,186,153,0.18),0_18px_40px_-12px_rgba(168,186,153,0.5)] transition-transform hover:scale-[1.02]"
            >
              <Play className="w-5 h-5" /> Try the Live Demo
              <span className="hidden sm:inline-flex ml-1 text-[10px] uppercase tracking-[0.18em] bg-[#1A2018] text-sage-300 rounded-full px-2 py-0.5">
                2 min · no signup
              </span>
            </Button>
          </div>
        </div>

        <div className="absolute bottom-0 inset-x-0 h-16 bg-gradient-to-b from-transparent to-[#FAFAF9]" />
      </section>

      {/* ---------- Section 2: Our Tools ---------- */}
      <section
        id="tools"
        data-testid="mpc-tools"
        className="relative py-20 sm:py-28 bg-[#FAFAF9] text-[#1C1C1C] overflow-hidden"
      >
        {/* Soft sage radial accents — much subtler against a light bg */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.35]" aria-hidden="true" style={{
          backgroundImage:
            "radial-gradient(circle at 15% 20%, rgba(168,186,153,0.20) 0%, transparent 45%), radial-gradient(circle at 85% 80%, rgba(168,186,153,0.18) 0%, transparent 50%)",
        }} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-8">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-sage-700 bg-sage-100 border border-sage-200 rounded-full px-3 py-1">
              <Sparkles className="w-3 h-3" /> Our tools
            </div>
            <h2 className="fc-heading text-[#1C1C1C] text-3xl sm:text-5xl font-medium tracking-tight mt-5 leading-[1.05]">
              Two focused tools.<br className="hidden sm:block" />
              <span className="text-sage-700">One decision engine.</span>
            </h2>
            <p className="text-base text-[#57534E] mt-4 max-w-xl leading-relaxed">
              Pick the tool that matches your project scope. Click anywhere on a card to launch.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-8">
            {/* SpaceCost Card first — single-room jobs are the more common
                entry point so we surface it ahead of FlipCost on every
                page that lists both tools. */}
            <ToolCard
              tid="mpc-tool-spacecost"
              mediaSlot={<SpaceCostMockup />}
              title="SpaceCost"
              tagline="Single-room & single-space budgets"
              pillLabel="Available now"
              pillTone="active"
              description="Cost a single space — before you swing the first hammer. SpaceCost is a room-by-room renovation budgeting tool that helps you forecast costs with clarity — breaking your project into structured categories and line items so you can plan accurately and stay in control."
              features={[
                "Up to 3 rooms per project",
                "Estimate vs Actual mode",
                "Editable cost library",
                "PDF-friendly room reports",
              ]}
              primaryLabel="Open SpaceCost"
              onPrimary={() => goToSpaceCost(navigate)}
              secondaryLabel="See pricing"
              onSecondary={() => navigate("/spacecost/pricing")}
              onCardClick={() => goToSpaceCost(navigate)}
            />

            {/* FlipCost Card */}
            <ToolCard
              tid="mpc-tool-flipcost"
              image={FLIPCOST_IMAGE}
              title="FlipCost"
              tagline="Whole-property renovations & flips"
              pillLabel="Available now"
              pillTone="live"
              description="A smarter way to budget and assess feasibility. FlipCost helps you estimate the cost of renovating or flipping properties, with structured cost breakdowns, clear project insights, and the ability to manage your project budget — helping you make informed decisions before you start."
              features={[
                "Room-by-room cost estimates",
                "Estimate vs Actual budget tracking",
                "Profit, ROI & resale insights",
                "Multi-currency, region-aware rates",
              ]}
              primaryLabel="Open FlipCost"
              onPrimary={() => navigate("/flipcost")}
              secondaryLabel="See pricing"
              onSecondary={() => navigate("/pricing")}
              onCardClick={() => navigate("/flipcost")}
            />
          </div>
        </div>
      </section>

      {/* ---------- Section 3: About ---------- */}
      <section id="about" data-testid="mpc-about" className="relative py-20 sm:py-28 bg-[#0F0E0C] text-white overflow-hidden">
        {/* Subtle sage atmospherics so the dark block doesn't feel flat */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.14]" aria-hidden="true" style={{
          backgroundImage:
            "radial-gradient(circle at 85% 15%, rgba(168,186,153,0.55) 0%, transparent 45%), radial-gradient(circle at 10% 90%, rgba(168,186,153,0.45) 0%, transparent 50%)",
        }} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-12 items-start">
            <div>
              <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-sage-300 bg-sage-300/10 border border-sage-300/30 rounded-full px-3 py-1">
                About MyPlanCost
              </div>
              <h2 className="fc-heading text-3xl sm:text-5xl font-medium tracking-tight text-white mt-5 leading-[1.1]">
                Tools designed for better project decisions.
              </h2>
              <p className="text-base sm:text-lg text-white/75 mt-6 leading-relaxed max-w-xl">
                Our tools are designed to help you understand what your project will cost and whether it makes sense — before you start. From full renovations to individual spaces, get structured estimates, clear breakdowns, and the insight needed to plan with confidence.
              </p>
              <div className="mt-7 flex flex-wrap items-center gap-3" data-testid="mpc-about-help">
                <Button
                  data-testid="mpc-about-how-btn"
                  onClick={() => setPicker("how-it-works")}
                  className="bg-sage-300 text-[#1A1917] hover:bg-sage-200 h-11 px-5 rounded-lg text-sm font-medium inline-flex items-center gap-2"
                >
                  <BookOpen className="w-4 h-4" /> How it works
                </Button>
                <Button
                  data-testid="mpc-about-faq-btn"
                  onClick={() => setPicker("faq")}
                  variant="outline"
                  className="bg-transparent text-white border-white/25 hover:border-white/50 hover:bg-white/10 hover:text-white h-11 px-5 rounded-lg text-sm font-medium inline-flex items-center gap-2"
                >
                  <HelpCircle className="w-4 h-4" /> FAQs
                </Button>
              </div>
              <p className="text-[12px] text-white/45 mt-2">Pick the tool you want to read about — FlipCost or SpaceCost.</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <ValueCard
                tid="mpc-value-clarity"
                icon={Sparkles}
                title="Clarity, not guesswork"
                body="Structured cost estimates tuned per region — so the first number you see is close to the last."
              />
              <ValueCard
                tid="mpc-value-feasibility"
                icon={TrendingUp}
                title="Feasibility-first"
                body="Test profit, margin and ROI before you commit capital or pick up a hammer."
              />
              <ValueCard
                tid="mpc-value-scopes"
                icon={Ruler}
                title="Whole-home or single-space"
                body="Whether you're flipping a house or tiling one bathroom, there's a tool that fits the scope."
              />
              <ValueCard
                tid="mpc-value-confidence"
                icon={ShieldCheck}
                title="Plan with confidence"
                body="Every estimate is editable, every assumption transparent. Your numbers, your call."
              />
            </div>
          </div>
        </div>
      </section>

      {/* ---------- Section 4: Call to Action ---------- */}
      <section data-testid="mpc-cta" className="py-20 sm:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-8">
          <div className="relative rounded-2xl overflow-hidden bg-[#1C1C1C] text-white px-8 sm:px-14 py-14 sm:py-20 text-center">
            <div className="absolute inset-0 opacity-[0.06]" style={{
              backgroundImage:
                "radial-gradient(ellipse at top, #A8B8A0 0%, transparent 60%), radial-gradient(ellipse at bottom, #A8B8A0 0%, transparent 60%)",
            }} />
            <div className="relative">
              <h2 className="fc-heading text-3xl sm:text-5xl font-medium tracking-tight leading-[1.1]">
                Start planning your project today.
              </h2>
              <p className="text-white/75 mt-5 max-w-xl mx-auto text-base sm:text-lg leading-relaxed">
                Get clarity on your costs and make better decisions before you begin.
              </p>
              <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
                <Button
                  data-testid="mpc-cta-get-started"
                  onClick={() => navigate("/projects")}
                  className="bg-white text-[#1C1C1C] hover:bg-[#F5F5F4] h-12 px-7 rounded-lg text-[15px] font-medium inline-flex items-center gap-2"
                >
                  Get Started <ArrowRight className="w-4 h-4" />
                </Button>
                <Button
                  data-testid="mpc-cta-demo"
                  onClick={openDemo}
                  className="bg-sage-300 text-[#1A1917] hover:bg-sage-200 h-12 px-7 rounded-lg text-[15px] font-medium inline-flex items-center gap-2"
                >
                  <Play className="w-4 h-4" /> Try the Live Demo
                </Button>
                <button
                  type="button"
                  data-testid="mpc-cta-tools"
                  onClick={() => scrollToId("tools")}
                  className="text-sm text-white/75 hover:text-white underline underline-offset-4"
                >
                  Compare our tools
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- Section 5: Contact ---------- */}
      <section id="contact" data-testid="mpc-contact" className="py-20 sm:py-24 bg-sage-50/60 border-t border-sage-200/60">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-12 items-start">
          <div>
            <div className="fc-label">Contact</div>
            <h2 className="fc-heading text-3xl sm:text-4xl font-medium tracking-tight text-[#1C1C1C] mt-3 leading-[1.1]">
              Get in touch.
            </h2>
            <p className="text-[#57534E] mt-4 leading-relaxed max-w-md">
              Have a question or need help? Send us a note and we'll get back to you shortly.
            </p>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              data-testid="mpc-contact-email"
              className="mt-6 inline-flex items-center gap-2 text-sm text-[#1C1C1C] hover:text-sage-700 transition-colors"
            >
              <Mail className="w-4 h-4" /> {CONTACT_EMAIL}
            </a>
          </div>

          <form
            onSubmit={onSubmit}
            data-testid="mpc-contact-form"
            className="fc-card p-6 sm:p-8 space-y-4 bg-white"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="fc-label">Name</label>
                <Input
                  data-testid="mpc-contact-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your full name"
                  className="fc-input mt-1.5 h-11"
                  required
                />
              </div>
              <div>
                <label className="fc-label">Email</label>
                <Input
                  data-testid="mpc-contact-email-input"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="name@example.com"
                  className="fc-input mt-1.5 h-11"
                  required
                />
              </div>
            </div>
            <div>
              <label className="fc-label">Message</label>
              <Textarea
                data-testid="mpc-contact-message"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Tell us a bit about your project or question…"
                className="fc-input mt-1.5 min-h-[150px] text-sm leading-relaxed"
                required
              />
            </div>
            <div className="flex items-center justify-between pt-1">
              <div className="text-[11px] text-[#A8A29E]">
                We'll only use your email to reply.
              </div>
              <Button
                type="submit"
                data-testid="mpc-contact-submit"
                disabled={submitting}
                className="bg-[#1C1C1C] text-white hover:bg-[#2A2A2E] h-11 px-5 rounded-lg text-sm font-medium inline-flex items-center gap-2"
              >
                {submitting ? "Sending…" : (<>Send Message <Send className="w-4 h-4" /></>)}
              </Button>
            </div>
          </form>
        </div>
      </section>

      {/* ---------- Section 6: Footer ---------- */}
      <footer data-testid="mpc-footer" className="bg-[#1C1C1C] text-white/70">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-14 grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="col-span-2">
            <div className="flex items-center gap-2.5 text-white">
              <div
                className="w-12 h-12 rounded-xl overflow-hidden bg-white shrink-0"
                style={{ filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.3))" }}
              >
                <img
                  src={LOGO_IMAGE}
                  alt="MyPlanCost"
                  className="w-full h-full object-cover scale-[1.12]"
                />
              </div>
              <span className="fc-heading text-base font-medium">MyPlanCost</span>
            </div>
            <p className="text-[13px] mt-4 max-w-sm leading-relaxed">
              MyPlanCost helps you plan, budget and assess your project before you commit. Tools built for clarity — not guesswork.
            </p>
          </div>
          <div>
            <div className="text-white text-[12px] uppercase tracking-[0.16em] font-medium">Navigate</div>
            <ul className="mt-4 space-y-2.5 text-[13px]">
              <li><button data-testid="mpc-footer-home" onClick={() => scrollToId("hero")} className="hover:text-white transition-colors">Home</button></li>
              <li><button data-testid="mpc-footer-tools" onClick={() => scrollToId("tools")} className="hover:text-white transition-colors">Tools</button></li>
              <li><button data-testid="mpc-footer-pricing" onClick={() => navigate("/pricing")} className="hover:text-white transition-colors">Pricing</button></li>
              <li><button data-testid="mpc-footer-about" onClick={() => scrollToId("about")} className="hover:text-white transition-colors">About</button></li>
              <li><button data-testid="mpc-footer-contact" onClick={() => scrollToId("contact")} className="hover:text-white transition-colors">Contact</button></li>
            </ul>
          </div>
          <div>
            <div className="text-white text-[12px] uppercase tracking-[0.16em] font-medium">Our Tools</div>
            <ul className="mt-4 space-y-2.5 text-[13px]">
              <li><button data-testid="mpc-footer-flipcost" onClick={() => navigate("/flipcost")} className="hover:text-white transition-colors">FlipCost</button></li>
              <li><button data-testid="mpc-footer-spacecost" onClick={() => goToSpaceCost(navigate)} className="hover:text-white transition-colors">SpaceCost</button></li>
              <li><button data-testid="mpc-footer-disclaimer" onClick={() => navigate("/disclaimer")} className="hover:text-white transition-colors">Disclaimer</button></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-6xl mx-auto px-4 sm:px-8 py-5 flex flex-wrap items-center justify-between gap-3 text-[12px] text-white/50">
            <div>© {new Date().getFullYear()} MyPlanCost. All rights reserved.</div>
            <div>Estimates only. See the <button onClick={() => navigate("/disclaimer")} className="underline hover:text-white/80">full disclaimer</button> for details.</div>
          </div>
        </div>
      </footer>

      <DemoChooserDialog
        open={chooserOpen}
        onOpenChange={setChooserOpen}
        onPick={startDemoForProduct}
      />
      <DemoDialog open={demoOpen} onOpenChange={setDemoOpen} product={demoProduct} />

      {/* Sticky "Try the Demo" pill — appears once the user scrolls past the
          hero. Lives in the bottom-right (above the HelpPill which sits in
          the same corner). Gives anyone half-way down the page a one-click
          path back to the demo without scrolling back up. */}
      {showStickyDemo && (
        <button
          type="button"
          data-testid="mpc-sticky-demo-cta"
          onClick={openDemo}
          className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 bg-sage-300 hover:bg-sage-200 text-[#1A1917] text-sm font-semibold h-12 px-5 rounded-full shadow-[0_18px_40px_-12px_rgba(168,186,153,0.55),0_0_0_4px_rgba(168,186,153,0.18)] transition-all hover:scale-[1.04]"
        >
          <Play className="w-4 h-4" /> Try the Demo
        </button>
      )}

      <ToolPickerDialog
        purpose={picker}
        onClose={() => setPicker(null)}
        onPick={(tool) => {
          const dest = picker === "faq" ? "faq" : "how-it-works";
          setPicker(null);
          if (tool === "spacecost") {
            // SpaceCost has its own How-It-Works / FAQ pages now.
            const dest = picker === "faq" ? "/spacecost/faq" : "/spacecost/how-it-works";
            navigate(dest);
          } else {
            navigate(`/${dest}`);
          }
        }}
      />
    </div>
  );
}

// Picker shown after the user clicks "How it works" or "FAQs" in the About
// section. Lets them choose which product (FlipCost or SpaceCost) they want
// to read about, then routes accordingly.
function ToolPickerDialog({ purpose, onClose, onPick }) {
  const open = !!purpose;
  const heading = purpose === "faq" ? "FAQs" : "How it works";
  const intro = purpose === "faq"
    ? "Which product's FAQ would you like to read?"
    : "Which product would you like to learn about?";

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        data-testid="mpc-tool-picker"
        className="bg-white text-[#27272A] sm:max-w-md"
      >
        <DialogHeader>
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-sage-700 bg-sage-100 border border-sage-200 rounded-full px-2.5 py-0.5 w-fit">
            {heading}
          </div>
          <DialogTitle className="fc-heading text-[#1C1C1C] mt-3">{intro}</DialogTitle>
          <DialogDescription className="text-xs text-[#A8A29E]">
            FlipCost is for whole-property renovations and flips. SpaceCost is for single-room or single-space budgets — launching soon.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
          <button
            type="button"
            data-testid="mpc-tool-picker-flipcost"
            onClick={() => onPick("flipcost")}
            className="text-left rounded-xl border-2 border-sage-300 bg-sage-50/40 hover:bg-sage-50 p-5 transition-all"
          >
            <div className="flex items-center gap-2 text-sage-700">
              <div className="w-8 h-8 rounded-lg bg-sage-100 border border-sage-200 grid place-items-center">
                <Building2 className="w-4 h-4" />
              </div>
              <div className="fc-label text-sage-700">FlipCost</div>
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-sage-700 mt-3">Available now</div>
            <div className="text-[13px] text-[#1C1C1C] mt-1">Whole-property flips & renovations.</div>
            <div className="mt-3 inline-flex items-center gap-1 text-[12px] text-[#1C1C1C]">
              Open <ArrowRight className="w-3.5 h-3.5" />
            </div>
          </button>

          <button
            type="button"
            data-testid="mpc-tool-picker-spacecost"
            onClick={() => onPick("spacecost")}
            className="text-left rounded-xl border border-white/10 bg-[#1C1C1C] text-white hover:border-white/30 p-5 transition-all"
          >
            <div className="flex items-center gap-2 text-sage-300">
              <div className="w-8 h-8 rounded-lg bg-sage-300/15 border border-sage-300/30 grid place-items-center">
                <Ruler className="w-4 h-4" />
              </div>
              <div className="fc-label text-sage-300">SpaceCost</div>
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-sage-300 mt-3 inline-flex items-center gap-1">
              <Sparkles className="w-2.5 h-2.5" /> Coming soon
            </div>
            <div className="text-[13px] text-white/85 mt-1">Single-room & single-space budgets.</div>
            <div className="mt-3 inline-flex items-center gap-1 text-[12px] text-sage-300">
              Read more <ArrowRight className="w-3.5 h-3.5" />
            </div>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ValueCard({ tid, icon: Icon, title, body }) {
  return (
    <div className="p-5 rounded-xl bg-white/[0.04] border border-white/10 backdrop-blur-sm hover:bg-white/[0.07] hover:border-sage-300/40 transition-colors" data-testid={tid}>
      <div className="w-9 h-9 rounded-lg bg-sage-300/15 border border-sage-300/40 text-sage-300 grid place-items-center">
        <Icon className="w-4 h-4" />
      </div>
      <div className="fc-heading text-[15px] font-medium text-white mt-4">{title}</div>
      <p className="text-[13px] text-white/65 mt-1.5 leading-relaxed">{body}</p>
    </div>
  );
}

function ToolCard({
  tid, image, mediaSlot, title, tagline, pillLabel, pillTone, description, features = [],
  primaryLabel, onPrimary, secondaryLabel, onSecondary, onCardClick,
}) {
  const isComingSoon = pillTone === "soon";
  const pillClass = isComingSoon
    ? "bg-white/10 text-white border-white/20"
    : "bg-sage-300/15 text-sage-300 border-sage-300/40";
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onCardClick}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onCardClick?.()}
      data-testid={tid}
      className="group relative bg-[#1F1D1A] border border-sage-300/25 rounded-2xl overflow-hidden cursor-pointer shadow-[0_24px_60px_-25px_rgba(0,0,0,0.7)] ring-1 ring-white/[0.04] transition-all duration-300 hover:-translate-y-1.5 hover:border-sage-300/70 hover:shadow-[0_36px_70px_-20px_rgba(168,186,153,0.45)] focus:outline-none focus-visible:ring-2 focus-visible:ring-sage-300/60"
    >
      {/* Title strip — top of the card, ABOVE the image, so visitors see
          "FlipCost / SpaceCost" before scrolling past the screenshot. The
          sage accent bar makes each card feel like a distinct product
          shelf instead of two dark squares blending into the section bg. */}
      <div className="relative px-6 sm:px-8 pt-6 sm:pt-7 pb-4 border-b border-white/[0.06] bg-gradient-to-r from-sage-300/[0.08] via-transparent to-transparent">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="fc-heading text-3xl sm:text-4xl font-medium text-white tracking-tight leading-none">
              {title}
            </h3>
            {tagline && (
              <div className="text-[11px] text-sage-300 uppercase tracking-[0.18em] mt-2">
                {tagline}
              </div>
            )}
          </div>
          <span className={`shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] px-2.5 py-1 rounded-full border ${pillClass}`}>
            {pillLabel}
          </span>
        </div>
      </div>

      {/* Hero image (or custom mocked media slot — used by SpaceCost for a tilted UI preview) */}
      <div className="relative aspect-[16/10] overflow-hidden bg-[#0F0E0C]">
        {mediaSlot ? (
          <div className="absolute inset-0 transition-transform duration-700 group-hover:scale-[1.04]">
            {mediaSlot}
          </div>
        ) : (
          <img
            src={image}
            alt={title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
          />
        )}
        {/* Bottom-fade so the body content reads against the image edge */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#1F1D1A] via-transparent to-transparent pointer-events-none" />
        {/* Top-right chevron — communicates clickability */}
        <div
          className={`absolute top-4 right-4 w-10 h-10 rounded-full grid place-items-center border transition-all duration-300 ${
            isComingSoon
              ? "bg-white/10 border-white/20 text-white/80 group-hover:bg-white/15"
              : "bg-sage-300/15 border-sage-300/40 text-sage-300 group-hover:bg-sage-300 group-hover:text-[#1A1917] group-hover:scale-110"
          }`}
        >
          <ArrowUpRight className="w-4 h-4" />
        </div>
      </div>

      {/* Body */}
      <div className="p-7 sm:p-8">
        <p className="text-[14px] text-white/75 leading-relaxed">{description}</p>

        {features.length > 0 && (
          <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
            {features.map((f) => (
              <li key={f} className="flex items-start gap-2 text-[13px] text-white/85">
                <ShieldCheck className="w-3.5 h-3.5 text-sage-300 mt-[3px] shrink-0" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-7 flex flex-wrap items-center gap-2.5">
          <Button
            data-testid={`${tid}-primary`}
            onClick={(e) => { e.stopPropagation(); onPrimary?.(); }}
            className={`h-11 px-5 rounded-lg text-sm font-medium inline-flex items-center gap-1.5 ${
              isComingSoon
                ? "bg-white text-[#1A1917] hover:bg-white/90"
                : "bg-sage-300 text-[#1A1917] hover:bg-sage-200"
            }`}
          >
            {primaryLabel} <ArrowRight className="w-4 h-4" />
          </Button>
          {secondaryLabel && (
            <Button
              data-testid={`${tid}-secondary`}
              onClick={(e) => { e.stopPropagation(); onSecondary?.(); }}
              variant="outline"
              className="h-11 px-5 rounded-lg text-sm bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-white"
            >
              {secondaryLabel}
            </Button>
          )}
          <span className="ml-auto text-[11px] uppercase tracking-[0.18em] text-white/40 group-hover:text-sage-300 transition-colors hidden sm:inline-flex items-center gap-1.5">
            Click anywhere to open <ArrowRight className="w-3 h-3" />
          </span>
        </div>
      </div>
    </div>
  );
}
